import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { colors } from "../Assets/styles/colors";
import { useAuthService } from "../Context/AuthContext";
import { useRealtimeConversation } from "../hooks/RealtimeConversationClient";
import LanguageListSelector from "../Components/Auth Components/LanguageListSelector";
import { ReactComponent as Unfold } from "../Assets/icons/Unfold_More.svg";
import { useTranslate } from "@tolgee/react";
import VolumeStepper from "../Components/GenericComponents/VolumeStepper";
import SpeedControlCapsule from "../Components/GenericComponents/SpeedControlCapsule";
import { VOICE_SPEED_1X, VOICE_SPEED_1_25X, VOICE_SPEED_1_5X } from "../Constants/constantValues";
import {ReactComponent as EmotiiVoice} from "../Assets/iconsv2/controlButtons/emotiiVoice.svg"
import { useNotify } from "../Helpers/Notifications";
import { getBaseCode } from "../Helpers/languageHelper";
import { downloadFile } from "../Helpers/downloadHelper";
import DeleteConfirmation from "../Components/GenericComponents/DeleteConfirmations";
import MeetingsAccessGate from "../Components/GenericComponents/MeetingsAccessGate";
import { ConversationIcon } from "../Components/Tools/ToolIcons";

const ConversationTranslationPage = () => {
  const { t } = useTranslate();
  const { user, translationsLanguages, getTranslationsLanguages } = useAuthService();

  const [languageA, setLanguageA] = useState(null);
  const [languageB, setLanguageB] = useState(null);
  const [languageAOpen, setLanguageAOpen] = useState(false);
  const [languageBOpen, setLanguageBOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [volume, setVolume] = useState(100); 
  const [speed, setSpeed] = useState(VOICE_SPEED_1X);

  const messagesEndRef = useRef(null);
  const { notifyError, notifySuccess } = useNotify();

  const getNumericSpeed = (speedConst) => {
  if (speedConst === VOICE_SPEED_1_25X) return 1.25;
  if (speedConst === VOICE_SPEED_1_5X) return 1.5;
  return 1.0;
  };

  useEffect(() => {
    if (!translationsLanguages || translationsLanguages.length === 0) {
      if (getTranslationsLanguages) {
        getTranslationsLanguages(() => {}, () => {});
      }
    }
  }, [getTranslationsLanguages]);

  useEffect(() => {
    if (!translationsLanguages?.length) return;

    if (!languageA) {
      const defaultA = 
        translationsLanguages.find(l => l.name === user?.call_language) || 
        translationsLanguages.find(l => l.name === "English") || 
        translationsLanguages[0]; 
        
      setLanguageA(defaultA || null);
    }

    if (!languageB) {
      const defaultB = 
        translationsLanguages.find(l => l.name === "Romanian") || 
        translationsLanguages[1]; 
        
      setLanguageB(defaultB || null);
    }
  }, [translationsLanguages, user?.call_language]);

  const resolveLanguage = (nameOrObj) => {
    if (!nameOrObj) return null;
    if (typeof nameOrObj === 'object' && nameOrObj.locale) return nameOrObj;
    return translationsLanguages?.find(l => l.name === nameOrObj) || null;
  };

  const {
    isConnected,
    isListening,
    messages,
    startListening,
    stopListening,
    clearMessages
  } = useRealtimeConversation({
    languageA: getBaseCode(languageA),
    languageB: getBaseCode(languageB),
    ttsEnabled: true,
    speed: getNumericSpeed(speed),
    volume: volume / 100,
    user_id: user?.id
  });

  const handleClearMessages = () => {
    clearMessages();
    notifySuccess(t("translatePage.cleared"));
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const toggleListening = () => {
    if (!languageA || !languageB) {
      notifyError(t("live_conversation.missing_languages_alert"));
      return;
    }
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleSwapLanguages = () => {
    if (!languageA || !languageB) return; 
    setLanguageA(languageB);
    setLanguageB(languageA);
  };

  const handleDownloadTranscript = () => {
    if (!messages || messages.length === 0) {
      notifyError(t("live_conversation.no_download_alert"));
      return;
    }

    let fileContent = `${t("live_conversation.transcript_header")}\n`;
    fileContent += `${t("live_conversation.transcript_date")} ${new Date().toLocaleString()}\n`;
    fileContent += `${t("live_conversation.transcript_languages")} ${languageA?.name} & ${languageB?.name}\n`;
    fileContent += `-----------------------------------------\n\n`;

    messages.forEach((msg) => {
      const isLangA = msg.language && getBaseCode(languageA) === msg.language.toLowerCase();
      const speaker = isLangA ? languageA?.name : languageB?.name;
      const translation = msg.call_message_translations?.[0]?.translation || t("live_conversation.transcript_still_translating");

      fileContent += `[${speaker}]: ${msg.original_message}\n`;
      fileContent += `[${t("live_conversation.transcript_translated")}]: ${translation}\n\n`;
    });

    const rawName = t("live_conversation.export_filename").replace(".txt", "");
    const fileName = `${rawName}_${new Date().toISOString().slice(0, 10)}.txt`;

    downloadFile(fileContent, fileName, { isText: true });
    
    notifySuccess(t("live_feed.download_success"));
  };

  return (
    <MeetingsAccessGate title={t("tools_menu.conversation")} icon={<ConversationIcon />} hintKey="conversation">
    <PageWrapper>
      {languageAOpen && (
        <div style={{ position: "absolute", inset: 0, zIndex: 99999 }}>
          <LanguageListSelector
            isLanguageSelectorOpen={languageAOpen}
            openLanguageList={() => setLanguageAOpen(false)}
            valuesList={translationsLanguages || []}
            setValue={(val) => {
              const resolved = resolveLanguage(val);
              setLanguageA(resolved);
              setLanguageAOpen(false);
            }}
          />
        </div>
      )}

      {languageBOpen && (
        <div style={{ position: "absolute", inset: 0, zIndex: 99999 }}>
          <LanguageListSelector
            isLanguageSelectorOpen={languageBOpen}
            openLanguageList={() => setLanguageBOpen(false)}
            valuesList={translationsLanguages || []}
            setValue={(val) => {
              const resolved = resolveLanguage(val);
              setLanguageB(resolved);
              setLanguageBOpen(false);
            }}
          />
        </div>
      )}

      <Header>
        <HeaderRightActions>

          <DeleteConfirmation 
            handleRemoveItem={handleClearMessages}
            customMessage={t('live_conversation.confirm_clear')}
          />

          <IconButton title={t("live_conversation.export_title")} onClick={handleDownloadTranscript}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
              <polyline points="16 6 12 2 8 6"></polyline>
              <line x1="12" y1="2" x2="12" y2="15"></line>
            </svg>
          </IconButton>

          <VoicePillButton $isActive={showSettings} onClick={() => setShowSettings(!showSettings)}>
            <EmotiiVoice style={{ width: '20px', height: '20px' }} />
            {t("live_conversation.voice_label")}
          </VoicePillButton>
        </HeaderRightActions>
      </Header>

      {showSettings && (
        <>
          <div 
            style={{ position: "fixed", inset: 0, zIndex: 99 }} 
            onClick={() => setShowSettings(false)} 
          />

          <SettingsCard>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <h3 style={{ color: "white", fontSize: "18px", margin: 0, fontWeight: "600" }}>
                  {t("live_conversation.voice_settings_title")}
                </h3>
                <p style={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "12px", margin: 0, fontWeight: "400", letterSpacing: "0.2px" }}>
                  {t("live_conversation.voice_settings_subtitle")}
                </p>
              </div>
              
              <IconButton 
                onClick={() => setShowSettings(false)} 
                style={{ padding: "4px", background: "rgba(255,255,255,0.05)", marginTop: "-2px" }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </IconButton>
            </div>
            
            <SettingGroup>
              <SettingLabel>
                {t("live_conversation.volume")}
              </SettingLabel>
              <VolumeStepper value={volume} onChange={(e) => setVolume(parseInt(e.target.value, 10))} />
            </SettingGroup>
            
            <SettingGroup>
              <SettingLabel>
                {t("live_conversation.speed")}
              </SettingLabel>
              <SpeedControlCapsule 
                speed={speed} 
                onSpeedChange={(newSpeed) => setSpeed(newSpeed)}
                speeds={[
                  { label: "1.0x", value: VOICE_SPEED_1X },
                  { label: "1.25x", value: VOICE_SPEED_1_25X },
                  { label: "1.5x", value: VOICE_SPEED_1_5X },
                ]} 
              />
            </SettingGroup>
          </SettingsCard>
        </>
      )}

      <ChatArea>
        <MessageList>
          {messages.map((msg, index) => {
            const isLangA = msg.speakerSide === "A";
            const translatedText = msg.call_message_translations?.[0]?.translation;

            const spokenLang = translationsLanguages?.find(l => getBaseCode(l) === msg.from_lang_code);
            const targetLang = translationsLanguages?.find(l => getBaseCode(l) === msg.to_lang_code);

            const fallbackSpeaker = isLangA ? languageA?.name : languageB?.name;
            const fallbackTarget = isLangA ? languageB?.name : languageA?.name;

            const translationLabel = (spokenLang && targetLang) 
              ? `${spokenLang.name} → ${targetLang.name}`
              : `${fallbackSpeaker} → ${fallbackTarget}`;

            const isLastMessage = index === messages.length - 1;

            return (
              <MessageRow key={msg.id || index} $isLangA={isLangA}>
                <Bubble $isLangA={isLangA}>
                  <OriginalText>{msg.original_message}</OriginalText>
                  
                  {translatedText ? (
                    <>
                      <TranslatedText>{translatedText}</TranslatedText>
                      <LanguageTag>{translationLabel}</LanguageTag>
                    </>
                  ) : isLastMessage && isConnected ? (
                    <TypingIndicator>
                      <span />
                      <span />
                      <span />
                    </TypingIndicator>
                  ) : (
                    <TranslatedText style={{ opacity: 0.5, fontStyle: "italic", fontSize: "14px" }}>
                      {t("live_conversation.translation_failed")}
                    </TranslatedText>
                  )}
                  
                </Bubble>
              </MessageRow>
            );
          })}
          <div ref={messagesEndRef} />
        </MessageList>
      </ChatArea>

      <ControlBar>
        <WavesContainer $isListening={isListening}>
          <SvgWave $duration="15s" $isListening={isListening} viewBox="0 0 1200 120" preserveAspectRatio="none" style={{ filter: "blur(3px)" }}>
             <path d="M0 60 Q150 20 300 60 T600 60 T900 60 T1200 60 L1200 120 L0 120 Z" fill="rgba(255, 255, 255, 0.03)" />
          </SvgWave>
          
          <SvgWave $duration="10s" $isListening={isListening} viewBox="0 0 1200 120" preserveAspectRatio="none" style={{ filter: "blur(1px)" }}>
             <path d="M0 80 Q150 40 300 80 T600 80 T900 80 T1200 80 L1200 120 L0 120 Z" fill="rgba(255, 255, 255, 0.04)" />
          </SvgWave>
          
          <SvgWave $duration="7s" $isListening={isListening} viewBox="0 0 1200 120" preserveAspectRatio="none">
             <path d="M0 90 Q150 75 300 90 T600 90 T900 90 T1200 90" fill="none" stroke="rgba(255, 255, 255, 0.25)" strokeWidth="0.5" />
          </SvgWave>

          <SvgWave $duration="9s" $isListening={isListening} viewBox="0 0 1200 120" preserveAspectRatio="none">
             <path d="M0 90 Q150 105 300 90 T600 90 T900 90 T1200 90" fill="none" stroke={colors.purple || "#8e44ad"} opacity="0.8" strokeWidth="0.5" />
          </SvgWave>
        </WavesContainer>

        <StatusText>
          {isListening ? t("live_conversation.status_listening") : t("live_conversation.status_ready")}
        </StatusText>

        <ControlsRow style={{ zIndex: 1 }}>
          <LanguageSelectors>
            <LanguageTrigger onClick={() => setLanguageAOpen(true)}            >
              <LanguageName>{languageA?.name || t("live_conversation.select_language")}</LanguageName>
              <UnfoldIconWrapper><Unfold /></UnfoldIconWrapper>
            </LanguageTrigger>

            <SwapIcon onClick={handleSwapLanguages} title={t("live_conversation.swap_languages")}>⇄</SwapIcon>

            <LanguageTrigger onClick={() => setLanguageBOpen(true)}            >
              <LanguageName>{languageB?.name || t("live_conversation.select_language")}</LanguageName>
              <UnfoldIconWrapper><Unfold /></UnfoldIconWrapper>
            </LanguageTrigger>
          </LanguageSelectors>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
            <StatusLabel>
              {isListening 
                ? t("live_conversation.pause_listening") 
                : t("live_conversation.resume_listening")}
            </StatusLabel>

            <MicActionPill
              onClick={toggleListening}
              $isListening={isListening}
              disabled={!languageA || !languageB}
            >
              {isListening ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="4" y="4" width="16" height="16" rx="2" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                  <line x1="12" y1="19" x2="12" y2="23"></line>
                  <line x1="8" y1="23" x2="16" y2="23"></line>
                </svg>
              )}
            </MicActionPill>
          </div>
        </ControlsRow>
      </ControlBar>
    </PageWrapper>
    </MeetingsAccessGate>
  );
};

export default ConversationTranslationPage;

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background-color: transparent; 
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end; 
  padding: 17px 24px;
  background: rgba(28, 28, 35, 0.85);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  z-index: 10; 
`;

const HeaderRightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 50%;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const VoicePillButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: ${props => props.$isActive ? 'rgb(61, 58, 99)' : '#4b4b52'};  
  color: white;
  border: none;
  border-radius: 16px; 
  padding: 4px 10px; 
  font-size: 13px; 
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.$isActive ? 'rgb(76, 73, 114)' : '#5a5a63'};
  }
`;

const ChatArea = styled.div`
  flex: 1;
  overflow: hidden; 
  display: flex;
  flex-direction: column;
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.2);
    border-radius: 10px;
  }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
`;

const MessageRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: ${({ $isLangA }) => ($isLangA ? "flex-end" : "flex-start")};
  animation: ${slideUp} 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
`;

const Bubble = styled.div`
  max-width: min(80%, 600px);
  padding: 8px 14px;
  background-color: ${({ $isLangA }) => 
    $isLangA ? "rgb(61, 58, 99)" : "rgba(255, 255, 255, 0.1)"};
  color: white;
  
  border-radius: 16px;
  border-bottom-right-radius: ${({ $isLangA }) => ($isLangA ? "4px" : "18px")};
  border-bottom-left-radius: ${({ $isLangA }) => ($isLangA ? "18px" : "4px")};
  
  display: flex;
  flex-direction: column;
  gap: 4px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);

  overflow-wrap: break-word;  
  word-wrap: break-word;      
  word-break: break-word;     
`;

const OriginalText = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
  word-break: break-word;
  overflow-wrap: anywhere; 
`;

const TranslatedText = styled.span`
  font-size: 16px;
  font-weight: 500;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const LanguageTag = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 4px;
  text-align: left;
  letter-spacing: 0.3px;
`;

const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-4px); opacity: 1; }
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  padding: 4px 2px;
  height: 20px;

  span {
    width: 6px;
    height: 6px;
    background-color: white;
    border-radius: 50%;
    animation: ${bounce} 1.4s infinite ease-in-out both;
  }

  span:nth-child(1) { animation-delay: -0.32s; }
  span:nth-child(2) { animation-delay: -0.16s; }
  span:nth-child(3) { animation-delay: 0s; }
`;

const slideWave = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const WavesContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 360px;
  overflow: hidden;
  pointer-events: none; 
  z-index: 0;
  
  opacity: ${props => props.$isListening ? 1 : 0.4}; 
  transition: opacity 0.5s ease-in-out;
`;

const SvgWave = styled.svg`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 200%; 
  height: 100%;
  animation: ${slideWave} ${props => props.$duration} linear infinite;
  animation-play-state: ${props => props.$isListening ? 'running' : 'paused'}; 
  transform-origin: bottom center;
`;

const ControlBar = styled.div`
  position: relative;
  padding: 10px 24px 40px 24px; 
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: linear-gradient(180deg, rgba(30,30,35,0) 0%, rgba(18,18,18,1) 80%);
`;

const StatusText = styled.div`
  text-align: center;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 500;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
  z-index: 1;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 650px; 
  margin: 0 auto;
  
  background: rgba(28, 28, 35, 0.85); 
  border: 1px solid rgba(255, 255, 255, 0.08); 
  
  border-radius: 20px; 
  backdrop-filter: blur(16px); 
  -webkit-backdrop-filter: blur(16px);
  
  padding: 6px 6px 6px 24px; 
  box-shadow: 0 16px 40px rgba(0,0,0,0.6);
  z-index: 10;
`;

const LanguageSelectors = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px; 
`;


const LanguageTrigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between; 
  gap: 8px; 
  cursor: pointer;
  
  width: 135px; 
  padding: 4px 12px; 
  border-radius: 20px; 
  
  background: rgba(255, 255, 255, 0.05); 
  border: 1px solid rgba(255, 255, 255, 0.1);
  
`;

const LanguageName = styled.p`
  font-size: 15px; 
  font-weight: 500;
  margin: 0;
  color: #ffffff; 
  letter-spacing: 0.3px;
  
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UnfoldIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    height: 16px; 
    color: ${({ theme }) => theme?.menu?.primaryTextColor || "white"};
  }
`;


const SwapIcon = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 16px;
  cursor: pointer;
  padding: 5px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: white;
    transform: scale(1.1);
  }

  &:active {
    transform: rotate(180deg) scale(0.9);
  }
`;

const MicActionPill = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;  
  height: 40px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  
  background-color: rgb(61, 58, 99);
  color: white;
  box-shadow: none;

  &:hover {
    background-color: rgb(76, 73, 114);
  }
  
  &:disabled {
    background-color: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.2);
    cursor: not-allowed;
  }
`;

const MicText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

const slideInMenu = keyframes`
  from { opacity: 0; transform: translateY(-10px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const SettingsCard = styled.div`
  position: absolute;
  top: 60px;
  right: 24px;
  
  width: 320px;
  padding: 16px;
  
  background: rgba(28, 28, 35, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  z-index: 100;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  box-shadow: 0 20px 40px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.2);
  animation: ${slideInMenu} 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  transform-origin: top right;
`;

const SettingGroup = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SettingLabel = styled.div`
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  letter-spacing: 0.3px;

  svg {
    margin-right: 10px;
    color: ${({ theme }) => theme?.purple || "#8e44ad"};
  }
`;

const StatusLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

